import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { cn } from '../lib/utils';
import { Grid, Package, X, Search, Check } from 'lucide-react';

/**
 * VariantGridSelector - A grid-based variant selector for products with two numeric attributes
 * 
 * This component parses product sizes in formats like "3.75 x 10", "3.3 × 11.5", "3.75x10mm"
 * and displays them in a grid where rows = attribute1 (diameter) and columns = attribute2 (length)
 */
const VariantGridSelector = ({ 
  products, 
  inventory, 
  onSelect, 
  selectedProductId,
  isOpen,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('all');

  // Parse size string to extract two numeric dimensions
  const parseSize = (sizeStr) => {
    if (!sizeStr) return null;
    
    // Match patterns like "3.75 x 10", "3.3×11.5", "3.75x10mm", "3.75 × 10 mm"
    const match = sizeStr.match(/(\d+\.?\d*)\s*[x×]\s*(\d+\.?\d*)/i);
    if (match) {
      return {
        attr1: parseFloat(match[1]), // Diameter
        attr2: parseFloat(match[2])  // Length
      };
    }
    return null;
  };

  // Get stock for a product
  const getProductStock = (productId) => {
    const productInventory = inventory.filter(inv => inv.product_id === productId);
    return productInventory.reduce((sum, inv) => sum + (inv.quantity || 0), 0);
  };

  // Get all unique brands
  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(products.map(p => p.brand).filter(Boolean))];
    return uniqueBrands.sort();
  }, [products]);

  // Group products by base name (without size) and organize into grid data
  const gridData = useMemo(() => {
    // Filter products by search and brand
    let filteredProducts = products.filter(p => {
      const matchesSearch = searchTerm === '' || 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.brand?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = selectedBrand === 'all' || p.brand === selectedBrand;
      return matchesSearch && matchesBrand;
    });

    // Group products by base name (product name without size info)
    const groups = {};
    
    filteredProducts.forEach(product => {
      const parsed = parseSize(product.size);
      if (!parsed) {
        // Non-grid products (single variants or no size pattern)
        if (!groups['__single__']) {
          groups['__single__'] = { type: 'single', products: [] };
        }
        groups['__single__'].products.push({
          ...product,
          stock: getProductStock(product.id)
        });
        return;
      }

      // Extract base name (remove size from name if present in name)
      let baseName = product.name;
      // Try to extract base name by removing common size patterns from name
      baseName = baseName.replace(/\s*\d+\.?\d*\s*[x×]\s*\d+\.?\d*\s*(mm)?/gi, '').trim();
      baseName = baseName.replace(/\s+/g, ' ').trim();
      
      // If base name is empty, use product brand + generic name
      if (!baseName || baseName.length < 3) {
        baseName = product.brand ? `${product.brand} Implant` : 'Implant';
      }

      const groupKey = `${baseName}__${product.brand || 'Generic'}`;
      
      if (!groups[groupKey]) {
        groups[groupKey] = {
          type: 'grid',
          baseName,
          brand: product.brand,
          attr1Values: new Set(),
          attr2Values: new Set(),
          variants: {},
          hsn_code: product.hsn_code,
          gst_percentage: product.gst_percentage
        };
      }

      groups[groupKey].attr1Values.add(parsed.attr1);
      groups[groupKey].attr2Values.add(parsed.attr2);
      
      const key = `${parsed.attr1}_${parsed.attr2}`;
      groups[groupKey].variants[key] = {
        ...product,
        attr1: parsed.attr1,
        attr2: parsed.attr2,
        stock: getProductStock(product.id)
      };
    });

    // Convert sets to sorted arrays
    Object.keys(groups).forEach(key => {
      if (groups[key].type === 'grid') {
        groups[key].attr1Values = [...groups[key].attr1Values].sort((a, b) => a - b);
        groups[key].attr2Values = [...groups[key].attr2Values].sort((a, b) => a - b);
      }
    });

    return groups;
  }, [products, inventory, searchTerm, selectedBrand]);

  const handleVariantSelect = (product) => {
    if (product.stock > 0) {
      onSelect(product);
      onClose();
    }
  };

  const gridGroups = Object.entries(gridData).filter(([key, data]) => data.type === 'grid');
  const singleProducts = gridData['__single__']?.products || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="variant-grid-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="variant-grid-title">
            <Grid className="w-5 h-5 text-teal-600" />
            Select Product Variant
          </DialogTitle>
        </DialogHeader>

        {/* Search and Filter */}
        <div className="flex gap-3 py-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="variant-grid-search"
            />
          </div>
          {brands.length > 1 && (
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-md text-sm min-w-[150px]"
              data-testid="variant-grid-brand-filter"
            >
              <option value="all">All Brands</option>
              {brands.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto space-y-6 pr-2" data-testid="variant-grid-content">
          {/* Grid Groups */}
          {gridGroups.map(([groupKey, group]) => (
            <div key={groupKey} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`variant-group-${group.brand || 'generic'}`}>
              {/* Group Header */}
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900" data-testid="variant-group-name">{group.baseName}</h3>
                    <p className="text-xs text-slate-500">
                      {group.brand && <span className="mr-2" data-testid="variant-group-brand">{group.brand}</span>}
                      <span data-testid="variant-group-hsn">HSN: {group.hsn_code}</span>
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs" data-testid="variant-count-badge">
                    {Object.keys(group.variants).length} variants
                  </Badge>
                </div>
              </div>

              {/* Size Grid */}
              <div className="p-4 overflow-x-auto">
                <div className="min-w-fit">
                  {/* Header Row - Length Values */}
                  <div className="flex items-center mb-2">
                    <div className="w-20 flex-shrink-0 text-xs font-medium text-slate-500 text-right pr-2">
                      Ø / Length →
                    </div>
                    {group.attr2Values.map(length => (
                      <div 
                        key={length} 
                        className="w-20 flex-shrink-0 text-center text-xs font-semibold text-slate-700"
                      >
                        {length}mm
                      </div>
                    ))}
                  </div>

                  {/* Grid Rows - Diameter Values */}
                  {group.attr1Values.map(diameter => (
                    <div key={diameter} className="flex items-center mb-2">
                      {/* Row Header - Diameter */}
                      <div className="w-20 flex-shrink-0 text-xs font-semibold text-slate-700 text-right pr-2">
                        Ø{diameter}
                      </div>
                      
                      {/* Grid Cells */}
                      {group.attr2Values.map(length => {
                        const key = `${diameter}_${length}`;
                        const variant = group.variants[key];
                        const isSelected = variant && variant.id === selectedProductId;
                        const hasStock = variant && variant.stock > 0;
                        const isAvailable = Boolean(variant);

                        if (!isAvailable) {
                          return (
                            <div 
                              key={key}
                              className="w-20 h-16 flex-shrink-0 mx-0.5 rounded border border-dashed border-slate-200 bg-slate-50"
                            />
                          );
                        }

                        return (
                          <button
                            key={key}
                            onClick={() => handleVariantSelect(variant)}
                            disabled={!hasStock}
                            data-testid={`variant-cell-${diameter}-${length}`}
                            className={cn(
                              "w-20 h-16 flex-shrink-0 mx-0.5 rounded border-2 transition-all duration-150 flex flex-col items-center justify-center gap-0.5",
                              hasStock
                                ? isSelected
                                  ? "border-teal-500 bg-teal-50 ring-2 ring-teal-200"
                                  : "border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/50 cursor-pointer"
                                : "border-slate-200 bg-slate-100 cursor-not-allowed opacity-60"
                            )}
                          >
                            <span className={cn(
                              "text-xs font-semibold",
                              isSelected ? "text-teal-700" : hasStock ? "text-slate-700" : "text-slate-400"
                            )} data-testid={`variant-size-${diameter}-${length}`}>
                              {diameter}×{length}
                            </span>
                            <span className={cn(
                              "text-[10px]",
                              hasStock 
                                ? variant.stock <= 5 
                                  ? "text-amber-600 font-medium" 
                                  : "text-slate-500"
                                : "text-red-500"
                            )} data-testid={`variant-stock-${diameter}-${length}`}>
                              {hasStock ? `Stock: ${variant.stock}` : 'Out of stock'}
                            </span>
                            {isSelected && (
                              <Check className="w-3 h-3 text-teal-600" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Single Products (without grid pattern) */}
          {singleProducts.length > 0 && (
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="other-products-section">
              <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                <h3 className="font-semibold text-slate-900" data-testid="other-products-title">Other Products</h3>
                <p className="text-xs text-slate-500">Products without size variants</p>
              </div>
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {singleProducts.map(product => {
                  const isSelected = product.id === selectedProductId;
                  const hasStock = product.stock > 0;

                  return (
                    <button
                      key={product.id}
                      onClick={() => handleVariantSelect(product)}
                      disabled={!hasStock}
                      data-testid={`other-product-${product.id}`}
                      className={cn(
                        "p-3 rounded-lg border-2 transition-all duration-150 text-left",
                        hasStock
                          ? isSelected
                            ? "border-teal-500 bg-teal-50"
                            : "border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/50"
                          : "border-slate-200 bg-slate-100 cursor-not-allowed opacity-60"
                      )}
                    >
                      <p className={cn(
                        "text-sm font-medium truncate",
                        isSelected ? "text-teal-700" : "text-slate-700"
                      )}>
                        {product.name}
                      </p>
                      {product.size && (
                        <p className="text-xs text-slate-500 truncate">{product.size}</p>
                      )}
                      <p className={cn(
                        "text-xs mt-1",
                        hasStock 
                          ? product.stock <= 5 
                            ? "text-amber-600" 
                            : "text-slate-500"
                          : "text-red-500"
                      )}>
                        {hasStock ? `Stock: ${product.stock}` : 'Out of stock'}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {gridGroups.length === 0 && singleProducts.length === 0 && (
            <div className="text-center py-12" data-testid="variant-grid-empty-state">
              <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No products found</p>
              <p className="text-sm text-slate-400">Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose} data-testid="variant-grid-cancel-btn">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VariantGridSelector;
